import { useCallback, useMemo } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { ApolloClient } from '@apollo/client'
import {
  AddToWatchListMutation,
  AddToWatchListMutationVariables,
  DeleteFromWatchListMutation,
  DeleteFromWatchListMutationVariables,
  GetContentInWatchListQuery,
  GetContentInWatchListQueryVariables,
  GetDefaultWatchListQuery,
} from '@/types/codegen-federation'
import { ADD_TO_WATCHLIST, CONTENT_IN_WATCHLIST, DELETE_FROM_WATCHLIST, GET_DEFAULT_WATCHLIST } from './queries'

export const useGetDefaultWatchlist = ({ client }: { client: ApolloClient<object> }) => {
  const { data, loading } = useQuery<GetDefaultWatchListQuery>(GET_DEFAULT_WATCHLIST, {
    client,
    fetchPolicy: 'network-only',
    context: {
      requireAuth: true,
    },
    errorPolicy: 'all',
  })

  const defaultWatchlist = useMemo(() => {
    const filteredItems =
      data?.getMyDefaultWatchList?.items.edges
        .map((item) => item.node.content)
        .filter(
          (item): item is NonNullable<typeof item> & { project: { id: string; slug: string } } => !!item?.project.slug,
        ) ?? []
    return filteredItems
  }, [data?.getMyDefaultWatchList?.items.edges])

  return { defaultWatchlist, loading }
}

export const useIsTitleInWatchlist = ({ titleId, client }: { titleId: string; client: ApolloClient<object> }) => {
  const { data, refetch } = useQuery<GetContentInWatchListQuery, GetContentInWatchListQueryVariables>(
    CONTENT_IN_WATCHLIST,
    {
      client,
      variables: { contentId: titleId },
      context: {
        requireAuth: true,
      },
      errorPolicy: 'all',
    },
  )

  return { isInWatchlist: !!data?.contentInWatchList, refetch }
}

export const useEditWatchlist = ({ client }: { client: ApolloClient<object> }) => {
  const [addToList, { loading: addToListLoading }] = useMutation<
    AddToWatchListMutation,
    AddToWatchListMutationVariables
  >(ADD_TO_WATCHLIST, {
    client,
    refetchQueries: [{ query: GET_DEFAULT_WATCHLIST }],
    awaitRefetchQueries: true,
    context: {
      requireAuth: true,
    },
  })

  const [deleteFromList, { loading: deleteFromListLoading }] = useMutation<
    DeleteFromWatchListMutation,
    DeleteFromWatchListMutationVariables
  >(DELETE_FROM_WATCHLIST, {
    client,
    refetchQueries: [{ query: GET_DEFAULT_WATCHLIST }],
    awaitRefetchQueries: true,
    context: {
      requireAuth: true,
    },
    errorPolicy: 'all',
  })

  const addToWatchlist = useCallback(
    (contentId: string) => addToList({ variables: { contentId }, errorPolicy: 'all' }),
    [addToList],
  )

  const deleteFromWatchlist = useCallback(
    (contentId: string) => deleteFromList({ variables: { contentId }, errorPolicy: 'all' }),
    [deleteFromList],
  )

  return { addToWatchlist, deleteFromWatchlist, addToListLoading, deleteFromListLoading }
}
